import { Box, IconButton, Modal, Typography } from '@mui/material';
import React from 'react';
import './RejectModal.css';
import closeIcon from '../../../../assets/Admin/RequestImages/close-icon.svg';
import rejectIcon from '../../../../assets/Admin/RequestImages/reject-icon.svg';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { axiosInstance } from '../../../../utils/tokenUtils';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 300,
  bgcolor: 'background.paper',
  boxShadow: 24,
};

// Predefined rejection reasons for permanent rejection
const permanentRejectionReasons = [
  { value: '', label: 'Select a reason...' },
  { value: 'incomplete_documents', label: 'Incomplete or missing documents' },
  { value: 'insufficient_qualifications', label: 'Insufficient qualifications' },
  { value: 'experience_requirements', label: 'Does not meet experience requirements' },
  { value: 'background_check', label: 'Background check issues' },
  { value: 'interview_performance', label: 'Unsatisfactory interview performance' },
  { value: 'technical_skills', label: 'Inadequate technical skills' },
  { value: 'communication_skills', label: 'Poor communication skills' },
  { value: 'availability_mismatch', label: 'Schedule/availability does not match requirements' },
  { value: 'references', label: 'Negative or inadequate references' },
  { value: 'other', label: 'Other (please specify)' },
];

// Predefined reasons for re-verification
const reVerificationReasons = [
  { value: '', label: 'Select a reason...' },
  { value: 'documents_unclear', label: 'Documents are unclear or hard to read' },
  { value: 'missing_information', label: 'Missing required information' },
  { value: 'verification_needed', label: 'Additional verification needed' },
  { value: 'update_required', label: 'Information needs to be updated' },
  { value: 'clarification_needed', label: 'Clarification needed on submitted details' },
  { value: 'other', label: 'Other (please specify)' },
];

const customToast = message => {
  toast.custom(
    t => (
      <div className="flex bg-[#F1FFF4] shadow-lg rounded-lg mt-[40px]">
        <div className="flex-1 w-full p-4 flex items-center gap-4">
          <div className="close-icon-container">
            <CloseIcon style={{ color: '#FFF', width: '14px', height: '14px' }} />
          </div>
          <p className="text-sm font-medium text-gray-900">{message}</p>
          <div
            onClick={() => toast.dismiss(t.id)}
            className="w-[14px] h-[14px] cursor-pointer rounded-full flex items-center justify-center border border-[#D5DEE3] p-2"
            role="button"
            tabIndex={0}
          >
            <CloseIcon style={{ color: '#D5DEE3', width: '14px', height: '14px' }} />
          </div>
        </div>
      </div>
    ),
    {
      duration: 3000,
      position: 'top-right',
      id: 'custom-toast',
    }
  );
};

const RejectModal = ({ setOpenRejectModal, rowId, teacherName }) => {
  const navigate = useNavigate();
  const [actionType, setActionType] = useState('reject_permanently'); // 'reject_permanently' or 'request_reverification'
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [reasonText, setReasonText] = useState('');

  const handleClose = () => {
    setOpenRejectModal(false);
  };

  const handleActionTypeChange = type => {
    setActionType(type);
    setReason('');
    setCustomReason('');
  };

  const handleReasonChange = e => {
    setReason(e.target.value);
    if (e.target.value !== 'other') {
      setCustomReason('');
      // Auto-fill the reason text field with selected dropdown option
      const reasons =
        actionType === 'reject_permanently' ? permanentRejectionReasons : reVerificationReasons;
      const selectedReason = reasons.find(r => r.value === e.target.value);
      if (selectedReason && selectedReason.value !== '') {
        setReasonText(selectedReason.label);
      }
    } else {
      setReasonText('');
    }
  };

  const handleCustomReasonChange = e => {
    setCustomReason(e.target.value);
    setReasonText(e.target.value);
  };

  const handleReasonTextChange = e => {
    setReasonText(e.target.value);
  };

  const handleConfirm = async () => {
    try {
      if (!reasonText.trim()) {
        toast.error('Please provide a reason');
        return;
      }

      const approvalStatus =
        actionType === 'reject_permanently' ? 'rejected' : 'requested_for_reverification';

      // Prepare request body based on action type
      const requestBody = {
        approval_status: approvalStatus,
      };

      // Add the appropriate reason field based on action type
      if (actionType === 'reject_permanently') {
        requestBody.rejection_reason = reasonText;
      } else {
        requestBody.reverification_reason = reasonText;
      }

      const res = await axiosInstance.patch(`/admin/teachers/${rowId}/approval`, requestBody);

      if (res?.status === 200) {
        setOpenRejectModal(false);
        navigate('/admin/request');
        const message =
          actionType === 'reject_permanently'
            ? `${teacherName} request rejected permanently`
            : `Re-verification requested for ${teacherName}`;
        customToast(message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getCurrentReasons = () => {
    return actionType === 'reject_permanently' ? permanentRejectionReasons : reVerificationReasons;
  };

  return (
    <Modal
      open={true}
      // onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box
        sx={{
          ...style,
          width: '45%',
          minWidth: '500px',
          maxWidth: '600px',
          maxHeight: '90vh',
          fontFamily: 'Poppins',
          borderRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Fixed Header */}
        <Box className="modal-header">
          <Typography className="reject-application-text">Reject Application</Typography>
          <IconButton onClick={handleClose}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>

        {/* Scrollable Content Area */}
        <Box className="modal-scrollable-content">
          <Box className="main-content-area">
            <img src={rejectIcon} alt="reject" className="reject-icon-large" />
            <Typography className="request-reject-application-text">
              Please choose how you'd like to proceed — reject permanently or request
              re-verification with a note.
            </Typography>
          </Box>

          {/* Radio Button Options */}
          <Box className="radio-options-container">
            <div
              className={`radio-option ${actionType === 'reject_permanently' ? 'selected' : ''}`}
              onClick={() => handleActionTypeChange('reject_permanently')}
            >
              <div className="radio-button-container">
                <div
                  className={`radio-button ${actionType === 'reject_permanently' ? 'selected' : ''}`}
                >
                  {actionType === 'reject_permanently' && <div className="radio-dot"></div>}
                </div>
              </div>
              <div className="radio-content">
                <div className="radio-title">Reject Permanently</div>
                <div className="radio-subtitle">
                  This will permanently reject the application. This action cannot be undone.
                </div>
              </div>
            </div>

            <div
              className={`radio-option ${actionType === 'request_reverification' ? 'selected' : ''}`}
              onClick={() => handleActionTypeChange('request_reverification')}
            >
              <div className="radio-button-container">
                <div
                  className={`radio-button ${actionType === 'request_reverification' ? 'selected' : ''}`}
                >
                  {actionType === 'request_reverification' && <div className="radio-dot"></div>}
                </div>
              </div>
              <div className="radio-content">
                <div className="radio-title">Request Re-Verification</div>
                <div className="radio-subtitle">
                  Ask the applicant to resubmit or clarify specific details. You can include a note
                  below.
                </div>
              </div>
            </div>
          </Box>

          {/* Reason Section */}
          <Box className="reason-section">
            <Typography className="reject-application-text">Reason</Typography>

            {/* Dropdown for predefined reasons */}
            <select
              className="reject-application-dropdown"
              value={reason}
              onChange={handleReasonChange}
            >
              {getCurrentReasons().map(reasonOption => (
                <option key={reasonOption.value} value={reasonOption.value}>
                  {reasonOption.label}
                </option>
              ))}
            </select>

            {/* Reason Text Area */}
            <textarea
              placeholder="Write the reason..."
              className="reject-application-textarea"
              value={reasonText}
              onChange={handleReasonTextChange}
              rows={3}
            />

            {reason === 'other' && (
              <input
                type="text"
                placeholder="Please specify the reason..."
                className="reject-application-input custom-reason-input"
                value={customReason}
                onChange={handleCustomReasonChange}
              />
            )}
          </Box>
        </Box>

        {/* Fixed Buttons */}
        <Box className="modal-buttons-fixed">
          <button className="cancel-application-button-request" onClick={handleClose}>
            Cancel
          </button>
          <button className="confirm-application-button-request" onClick={handleConfirm}>
            Confirm
          </button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RejectModal;
